import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { I18nService } from '../../../services';
import { MarketingFacade } from '../../../facades/marketing.facade';
import { AppUtils } from '../../../utils/app.utils';
import { CustomerFacade } from '../../../facades/customer.facade';
import { DateUtils } from '../../../utils/date.utils';
import { DAYS_FROM_TODAY_TO_START_CONTRACT } from '../../../configurations/cpq';
import { EAddressType } from '../../../configurations/common';
import { CpqUtils } from '../../../utils/cpq.utils';
import { IAddress } from '../../../models/common.models';

@Component({
  selector: 'app-contract-details',
  templateUrl: './contract-details.component.html',
  styleUrls: ['./contract-details.component.scss'],
  standalone: false,
})
export class ContractDetailsComponent implements OnInit, OnDestroy {
  private i18nService: I18nService = inject(I18nService);
  private marketingFacade: MarketingFacade = inject(MarketingFacade);
  private customerFacade: CustomerFacade = inject(CustomerFacade);

  endOfSupportDate: string = '';
  contractStartDate: string = '';
  contractDurationYears: number = 5;
  shipAddress: string = '';
  shipToAddresses: IAddress[] = [];
  cpqAddressType: EAddressType = EAddressType.SHIP_TO;
  contractEndDateOptions: Array<{name: string; value: number; disabled: boolean}> = [];
  translations: any = [];
  minimumDaysFromToday: number = DAYS_FROM_TODAY_TO_START_CONTRACT;
  cart!: any;
  customerBusinessUnitName!: string;
  ecommerceTeamEmailAddress!: string;
  loadingCartDataInProgress: boolean = true;
  showWrongAddressModal: boolean = false;

  private unsubscribe$: Subject<void> = new Subject<void>();

  @Input() saveInProgress: boolean = false;
  @Input() showSaveDone: boolean = false;
  @Input() configId!: string;
  @Output() changed: EventEmitter<any> = new EventEmitter<any>();
  @Output() goBack: EventEmitter<any> = new EventEmitter<any>();
  @Output() goToReviewSummary: EventEmitter<any> = new EventEmitter<any>();

  ngOnInit(): void {
    this.marketingFacade.loadCurrentCart();
    this.selectLoadingCartDataInProgress();
    this.selectCart();
    this.getStoreEmailAddress();
    this.getUserData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  emitStartDate(event: any): void {
    this.changed.emit([
      {parameter: event.key, value: formatDate(event.value, 'yyyy-MM-dd', 'en')},
      {parameter: 'contractDurationYears', value: this.contractDurationYears},
    ]);
  }

  private getTranslations(): void {
    if (this.translations.length > 0) {
      this.configureEndDateOptions();
      return;
    }
    this.i18nService.getTranslationByKey([
      'service-configuration.year',
      'service-configuration.years',
      'service-configuration.until',
      'service-configuration.past',
    ]).pipe(takeUntil(this.unsubscribe$))
      .subscribe(translations => {
        this.translations = translations;
        this.configureEndDateOptions();
      });
  }

  private configureEndDateOptions(): void {
    const maxContractDurationYears: number = DateUtils.calculateContractTerm('', this.endOfSupportDate);
    this.contractEndDateOptions = Array.from({length: 10}).map((_param, index: number) => {
      const year: number = index + 1;
      const yearLabel: string = year === 1 ? this.translations['service-configuration.year']
        : this.translations['service-configuration.years'];
      if (!this.endOfSupportDate) {
        return {
          name: `${year} ${yearLabel}`,
          value: year,
          disabled: false,
        };
      }

      if (year < maxContractDurationYears) {
        return {
          name: `${year} ${yearLabel}`,
          value: year,
          disabled: false,
        };
      } else if (maxContractDurationYears === year) {
        return {
          name: `${year} ${yearLabel} ${this.translations['service-configuration.until']}`,
          value: year,
          disabled: false,
        };
      } else {
        return {
          name: `${year} ${yearLabel} ${this.translations['service-configuration.past']}`,
          value: year,
          disabled: true,
        };
      }
    });
  }

  selectLoadingCartDataInProgress(): void {
    this.marketingFacade.selectLoadingCartDataInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(loadingCartDataInProgress => {
        this.loadingCartDataInProgress = loadingCartDataInProgress;
      },
    );
  }

  getMultipleFunctionalLocations(cart: any): string[] {
    const functionalLocationsInArray: string[] = [];
    const functionalLocation: string = cart?.attributes?.systemDetails?.siemensEquipmentId ?? '';
    functionalLocationsInArray.push(functionalLocation);

    return functionalLocationsInArray;
  }

  showReportWrongShipToAddressModal(): void {
    this.showWrongAddressModal = true;
  }

  private selectCart(): void {
    this.marketingFacade.selectCart().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cart => {
        this.cart = cart;
        this.selectCartItems();
      },
    );
  }

  private selectCartItems(): void {
    this.marketingFacade.selectCartItemsWithDetails().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cartItems => {
        const currentItem: any = cartItems.find(item => item.attributes.cpqConfigId === this.configId);
        if (currentItem) {
          const {endOfSupport, contractDurationYears, contractStartDate} = currentItem.attributes.systemDetails;
          this.endOfSupportDate = endOfSupport ? formatDate(endOfSupport, 'dd-MMM-yyyy', 'en') : '';
          this.contractDurationYears = contractDurationYears ?? 5;
          this.contractStartDate = contractStartDate ?? '';
          this.shipAddress = CpqUtils.cpqAddressToString(currentItem.attributes.customerShipToAddress);
          this.shipToAddresses = currentItem.attributes.customerShipToAddress ? [currentItem.attributes.customerShipToAddress] : [];
          this.getTranslations();
        }
      },
    );
  }

  private getStoreEmailAddress(): void {
    this.ecommerceTeamEmailAddress = AppUtils.getCurrentStore().ecommerceTeamEmailAddress ?? '';
  }

  private getUserData(): void {
    this.customerFacade.selectCompanyBusinessUnit()
      .pipe(take(1))
      .subscribe(businessUnit => {
        this.customerBusinessUnitName = businessUnit?.name ?? '';
      });
  }

  updateContractDurationYears($event: any): void {
    this.contractDurationYears = $event.value;
    this.changed.emit($event);
  }
}
